// QueuePage — master-detail review queue for pending report drafts.
// Left rail: DraftListItem list. Right pane: commentary editor + preview + actions.
// Route params: /dashboard/queue (list only) and /dashboard/queue/:draftId (with selection).
// Polling every 5s keeps status dots live without a full page reload.

import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Banner } from "../components/Banner";
import { DraftListItem } from "../components/DraftListItem";
import type { DraftStatus } from "../components/DraftListItem";
import { CommentaryEditor } from "../components/CommentaryEditor";
import { EmailBodyOverride } from "../components/EmailBodyOverride";
import { ReportPreview } from "../components/ReportPreview";
import type { ReportPayload, ReportCtx } from "../components/ReportPreview";
import { QueueActions } from "../components/QueueActions";
import { BulkApproveButton } from "../components/BulkApproveButton";
import { api, ApiError } from "../lib/api";

// ─── API response shapes ─────────────────────────────────────────────────────

interface DraftListRow {
  draft_id: string;
  client_id: string;
  draft_type: string;
  account_id: string | null;
  cadence: "weekly" | "monthly";
  period_start: string;
  period_end: string;
  payload: string;
  commentary: string;
  email_body: string | null;
  status: DraftStatus;
  generated_at: string;
  approved_at: string | null;
  snooze_until: string | null;
  updated_at: string;
}

interface QueueListResponse {
  drafts: DraftListRow[];
}

interface DraftDetailResponse {
  draft: DraftListRow;
  latestRender: {
    render_id: string;
    delivery_status: string;
    delivered_email_id: string | null;
    rendered_at: string | null;
  } | null;
}

interface SaveResponse {
  ok: boolean;
  updated_at: string;
}

interface StatusResponse {
  statuses: Record<string, string>;
  truncated: boolean;
}

// ─── Derived state helpers ───────────────────────────────────────────────────

function alertCount(draft: DraftListRow): number {
  try {
    const p = JSON.parse(draft.payload) as { alert_counts?: { critical?: number; warning?: number; info?: number } };
    const c = p.alert_counts;
    if (!c) return 0;
    return (c.critical ?? 0) + (c.warning ?? 0);
  } catch {
    return 0;
  }
}

function parsePayload(draft: DraftListRow): ReportPayload | null {
  try {
    return JSON.parse(draft.payload) as ReportPayload;
  } catch {
    return null;
  }
}

function ctxFromDraft(draft: DraftListRow, commentary: string): ReportCtx {
  const p = parsePayload(draft);
  return {
    endAdvertiserName: p?.display_name ?? draft.account_id ?? "Draft",
    preparedBy: "Retrofit AI",
    periodStart: draft.period_start,
    periodEnd: draft.period_end,
    currency: p?.currency ?? "USD",
    cadence: draft.cadence,
    commentary,
  };
}

function displayName(draft: DraftListRow): string {
  try {
    const p = JSON.parse(draft.payload) as { display_name?: string };
    return p.display_name ?? draft.account_id ?? "Draft";
  } catch {
    return draft.account_id ?? "Draft";
  }
}

const POLL_INTERVAL_MS = 5000;

// ─── Component ───────────────────────────────────────────────────────────────

export function QueuePage() {
  const { draftId: routeDraftId } = useParams<{ draftId?: string }>();
  const navigate = useNavigate();

  const [drafts, setDrafts] = useState<DraftListRow[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedDraft, setSelectedDraft] = useState<DraftListRow | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [commentary, setCommentary] = useState("");
  const [emailBody, setEmailBody] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string>("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staleConflict, setStaleConflict] = useState(false);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tier 3 §5.1: capture the AI-draft baseline when the selected draft changes.
  // We snapshot draft.commentary only on draft-id change — not on every re-render —
  // so the baseline stays pinned to the value at load time.
  // Known limitation: if the user edits + saves + reloads, the baseline becomes
  // the saved value (no longer the original AI draft). A future `ai_draft_snapshot`
  // column would fix this, but it's out of scope for v1.
  const aiDraftBaselineRef = useRef<string>("");
  const lastSeenDraftId = useRef<string | null>(null);

  // ── Load the list ──────────────────────────────────────────────────────────

  const loadList = useCallback(async () => {
    try {
      const resp = await api<QueueListResponse>("/api/queue?status=pending_review");
      setDrafts(resp.drafts);
      setListError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load queue";
      setListError(msg);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // ── Capture AI-draft baseline when selected draft changes ─────────────────

  useEffect(() => {
    if (selectedDraft && selectedDraft.draft_id !== lastSeenDraftId.current) {
      aiDraftBaselineRef.current = selectedDraft.commentary ?? "";
      lastSeenDraftId.current = selectedDraft.draft_id;
    }
  }, [selectedDraft]);

  // ── Polling: update status dots every 5s ──────────────────────────────────

  useEffect(() => {
    if (pollTimerRef.current !== null) clearInterval(pollTimerRef.current);

    pollTimerRef.current = setInterval(async () => {
      if (drafts.length === 0) return;
      const ids = drafts.map((d) => d.draft_id).join(",");
      try {
        const resp = await api<StatusResponse>(`/api/queue/status?ids=${encodeURIComponent(ids)}`);
        setDrafts((prev) =>
          prev.map((d) => {
            const newStatus = resp.statuses[d.draft_id];
            return newStatus && newStatus !== d.status
              ? { ...d, status: newStatus as DraftStatus }
              : d;
          }),
        );
      } catch {
        // Swallow poll errors silently — the list still shows stale data
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current !== null) clearInterval(pollTimerRef.current);
    };
  }, [drafts.length, drafts.map((d) => d.draft_id).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load detail when route param changes ──────────────────────────────────

  useEffect(() => {
    if (!routeDraftId) {
      setSelectedDraft(null);
      return;
    }

    let cancelled = false;
    setLoadingDetail(true);
    setError(null);
    setStaleConflict(false);

    api<DraftDetailResponse>(`/api/queue/draft/${routeDraftId}`)
      .then((resp) => {
        if (cancelled) return;
        const d = resp.draft;
        setSelectedDraft(d);
        setCommentary(d.commentary ?? "");
        setEmailBody(d.email_body ?? null);
        setUpdatedAt(d.updated_at);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to load draft";
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [routeDraftId]);

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!selectedDraft) return;
    setSaving(true);
    setError(null);
    setStaleConflict(false);
    try {
      const resp = await api<SaveResponse>(`/api/queue/draft/${selectedDraft.draft_id}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commentary, email_body: emailBody, updated_at: updatedAt }),
      });
      setUpdatedAt(resp.updated_at);
      setSelectedDraft((prev) => (prev ? { ...prev, commentary, email_body: emailBody, updated_at: resp.updated_at } : prev));
      setDrafts((prev) =>
        prev.map((d) =>
          d.draft_id === selectedDraft.draft_id
            ? { ...d, commentary, email_body: emailBody, updated_at: resp.updated_at }
            : d,
        ),
      );
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 409) {
        setStaleConflict(true);
        setError("Draft was edited elsewhere — refresh to get the latest version.");
      } else {
        const msg = e instanceof Error ? e.message : "Save failed";
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [selectedDraft, commentary, emailBody, updatedAt]);

  // ── Refresh after 409 ─────────────────────────────────────────────────────

  const handleRefreshAfterConflict = useCallback(async () => {
    if (!selectedDraft) return;
    setStaleConflict(false);
    setError(null);
    setLoadingDetail(true);
    try {
      const resp = await api<DraftDetailResponse>(`/api/queue/draft/${selectedDraft.draft_id}`);
      const d = resp.draft;
      setSelectedDraft(d);
      setCommentary(d.commentary ?? "");
      setEmailBody(d.email_body ?? null);
      setUpdatedAt(d.updated_at);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Refresh failed";
      setError(msg);
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedDraft]);

  // ── Remove from list + auto-advance ──────────────────────────────────────

  const removeAndAdvance = useCallback(
    (removedId: string) => {
      setDrafts((prev) => {
        const idx = prev.findIndex((d) => d.draft_id === removedId);
        const next = prev.filter((d) => d.draft_id !== removedId);
        // Auto-select the draft that was below the removed one (or the new last)
        if (next.length > 0) {
          const nextDraft = next[Math.min(idx, next.length - 1)];
          if (nextDraft) {
            navigate(`/dashboard/queue/${nextDraft.draft_id}`, { replace: true });
          }
        } else {
          navigate("/dashboard/queue", { replace: true });
          setSelectedDraft(null);
        }
        return next;
      });
    },
    [navigate],
  );

  // ── Approve ───────────────────────────────────────────────────────────────

  const handleApprove = useCallback(async () => {
    if (!selectedDraft) return;
    setSaving(true);
    setError(null);
    try {
      await api(`/api/queue/draft/${selectedDraft.draft_id}/approve`, { method: "POST" });
      removeAndAdvance(selectedDraft.draft_id);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Approve failed";
      setError(msg);
      setSaving(false);
    }
  }, [selectedDraft, removeAndAdvance]);

  // ── Snooze ────────────────────────────────────────────────────────────────

  const handleSnooze = useCallback(async () => {
    if (!selectedDraft) return;
    setSaving(true);
    setError(null);
    try {
      await api(`/api/queue/draft/${selectedDraft.draft_id}/snooze`, { method: "POST" });
      removeAndAdvance(selectedDraft.draft_id);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Snooze failed";
      setError(msg);
      setSaving(false);
    }
  }, [selectedDraft, removeAndAdvance]);

  // ── Skip ──────────────────────────────────────────────────────────────────

  const handleSkip = useCallback(async () => {
    if (!selectedDraft) return;
    setSaving(true);
    setError(null);
    try {
      await api(`/api/queue/draft/${selectedDraft.draft_id}/skip`, { method: "POST" });
      removeAndAdvance(selectedDraft.draft_id);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Skip failed";
      setError(msg);
      setSaving(false);
    }
  }, [selectedDraft, removeAndAdvance]);

  // ── Regenerate ────────────────────────────────────────────────────────────

  const handleRegenerate = useCallback(async () => {
    if (!selectedDraft) return;
    setSaving(true);
    setError(null);
    try {
      await api(`/api/queue/draft/${selectedDraft.draft_id}/regenerate`, { method: "POST" });
      // Re-fetch to get the new payload (commentary is preserved server-side)
      const resp = await api<DraftDetailResponse>(`/api/queue/draft/${selectedDraft.draft_id}`);
      const d = resp.draft;
      setSelectedDraft(d);
      setCommentary(d.commentary ?? "");
      setEmailBody(d.email_body ?? null);
      setUpdatedAt(d.updated_at);
      // Also update the list row
      setDrafts((prev) =>
        prev.map((existing) => (existing.draft_id === d.draft_id ? d : existing)),
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Regenerate failed";
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [selectedDraft]);

  // ── Bulk approve ──────────────────────────────────────────────────────────

  const handleBulkApprove = useCallback(async () => {
    await api("/api/queue/bulk-approve", { method: "POST" });
    // Re-load the list — approved drafts are gone, any that had no commentary remain
    setLoadingList(true);
    await loadList();
    // If the currently selected draft was approved, clear the selection
    if (selectedDraft) {
      setDrafts((prev) => {
        const still = prev.find((d) => d.draft_id === selectedDraft.draft_id);
        if (!still) {
          navigate("/dashboard/queue", { replace: true });
          setSelectedDraft(null);
        }
        return prev;
      });
    }
  }, [loadList, selectedDraft, navigate]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const draftsWithCommentary = drafts.filter((d) => (d.commentary ?? "").trim().length > 0).length;
  const payload = selectedDraft ? parsePayload(selectedDraft) : null;
  const ctx = selectedDraft ? ctxFromDraft(selectedDraft, commentary) : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      {/* Page header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: "var(--space-5)",
        }}
      >
        <div>
          <span className="label">Report Review</span>
          <h1 style={{ marginTop: "6px" }}>Queue</h1>
        </div>
      </div>

      {listError && <Banner tone="err">{listError}</Banner>}
      {loadingList && !listError && <Banner tone="info">Loading queue…</Banner>}

      {!loadingList && !listError && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(240px, 320px) 1fr",
            gap: 24,
            alignItems: "start",
          }}
          className="queue-layout"
        >
          {/* ── Left rail ── */}
          <aside
            style={{
              borderRight: "1px solid rgba(255,255,255,0.06)",
              paddingRight: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0 12px 12px",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: "var(--fs-xs)",
                  fontFamily: "var(--font-mono)",
                  textTransform: "uppercase",
                  letterSpacing: "var(--letter-label)",
                  color: "var(--color-bone-dim)",
                  fontWeight: 600,
                }}
              >
                Pending review · {drafts.length}
              </span>
              <BulkApproveButton
                draftsWithCommentary={draftsWithCommentary}
                onConfirm={handleBulkApprove}
              />
            </div>

            {drafts.length === 0 && (
              <p
                style={{
                  padding: "24px 12px",
                  fontSize: "var(--fs-sm)",
                  color: "var(--color-bone-dim)",
                  textAlign: "center",
                }}
              >
                All caught up — no drafts pending review.
              </p>
            )}

            <div style={{ display: "flex", flexDirection: "column" }}>
              {drafts.map((d) => (
                <DraftListItem
                  key={d.draft_id}
                  draftId={d.draft_id}
                  endAdvertiserName={displayName(d)}
                  cadence={d.cadence}
                  generatedAt={d.generated_at}
                  status={d.status}
                  alertCount={alertCount(d)}
                  selected={routeDraftId === d.draft_id}
                  onClick={() => navigate(`/dashboard/queue/${d.draft_id}`)}
                />
              ))}
            </div>
          </aside>

          {/* ── Right pane ── */}
          <main style={{ minWidth: 0 }}>
            {!routeDraftId && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 200,
                  fontSize: "var(--fs-sm)",
                  color: "var(--color-bone-dim)",
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "var(--radius-sm)",
                }}
              >
                Select a draft from the list to review it.
              </div>
            )}

            {routeDraftId && loadingDetail && (
              <Banner tone="info">Loading draft…</Banner>
            )}

            {routeDraftId && !loadingDetail && error && !staleConflict && (
              <Banner tone="err">{error}</Banner>
            )}

            {routeDraftId && !loadingDetail && staleConflict && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "10px 14px",
                  background: "rgba(217,170,50,0.12)",
                  border: "1px solid rgba(217,170,50,0.3)",
                  borderRadius: "var(--radius-sm)",
                  marginBottom: 12,
                  fontSize: "var(--fs-sm)",
                  color: "#f5d782",
                }}
              >
                <span>{error}</span>
                <button
                  onClick={() => void handleRefreshAfterConflict()}
                  style={{
                    flexShrink: 0,
                    background: "rgba(217,170,50,0.2)",
                    border: "1px solid rgba(217,170,50,0.4)",
                    borderRadius: "var(--radius-sm)",
                    color: "#f5d782",
                    fontSize: "var(--fs-xs)",
                    fontFamily: "var(--font-mono)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "var(--letter-label)",
                    padding: "4px 10px",
                    cursor: "pointer",
                  }}
                >
                  Refresh
                </button>
              </div>
            )}

            {routeDraftId && !loadingDetail && selectedDraft && payload && ctx && (
              <>
                {/* Draft heading */}
                <div style={{ marginBottom: "var(--space-4)" }}>
                  <h2
                    style={{
                      fontSize: "var(--fs-lg)",
                      fontWeight: 700,
                      margin: "0 0 4px",
                    }}
                  >
                    {ctx.endAdvertiserName}
                  </h2>
                  <p
                    style={{
                      fontSize: "var(--fs-xs)",
                      fontFamily: "var(--font-mono)",
                      textTransform: "uppercase",
                      letterSpacing: "var(--letter-label)",
                      color: "var(--color-bone-dim)",
                      margin: 0,
                    }}
                  >
                    {selectedDraft.cadence === "weekly" ? "Weekly" : "Monthly"} ·{" "}
                    {selectedDraft.period_start} – {selectedDraft.period_end}
                  </p>
                </div>

                {/* Non-conflict errors */}
                {error && !staleConflict && (
                  <Banner tone="err" >{error}</Banner>
                )}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--space-4)",
                  }}
                >
                  <CommentaryEditor
                    value={commentary}
                    onChange={setCommentary}
                    onBlur={() => void handleSave()}
                    disabled={saving}
                    aiDraftValue={aiDraftBaselineRef.current}
                  />

                  <EmailBodyOverride
                    emailBody={emailBody}
                    onChange={setEmailBody}
                    disabled={saving}
                  />

                  <ReportPreview
                    payload={payload}
                    ctx={{ ...ctx, commentary }}
                    emailBody={emailBody}
                  />

                  <QueueActions
                    canApprove={commentary.trim().length > 0}
                    saving={saving}
                    onApprove={() => void handleApprove()}
                    onSave={() => void handleSave()}
                    onSnooze={() => void handleSnooze()}
                    onRegenerate={() => void handleRegenerate()}
                    onSkip={() => void handleSkip()}
                  />
                </div>
              </>
            )}
          </main>
        </div>
      )}

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .queue-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </Layout>
  );
}
